import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { navItems } from '../../routes';
import { usePermission } from '../../auth/usePermission';
import { NAV_ICONS, IconPanelToggle, IconChevronDown } from '../ui/icons';

function NavRow({ path, label, isActive, collapsed, nested = false }) {
  const Icon = NAV_ICONS[path] || NAV_ICONS.default;
  return (
    <NavLink
      to={path}
      end={path === '/'}
      title={collapsed ? label : undefined}
      className={({ isActive: linkActive }) =>
        `group relative flex items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition-colors ${
          collapsed ? 'justify-center px-0' : nested ? 'pl-10 pr-3' : 'px-3'
        } ${(isActive ?? linkActive) ? 'bg-white/[0.06] text-white' : 'text-gray-400 hover:bg-white/[0.04] hover:text-gray-100'}`
      }
    >
      {({ isActive: linkActive }) => {
        const active = isActive ?? linkActive;
        return (
          <>
            {!nested && (
              <span
                className={`absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary transition-opacity ${
                  active ? 'opacity-100 shadow-[0_0_10px_1px_rgba(220,38,38,0.65)]' : 'opacity-0'
                }`}
              />
            )}
            {!nested && (
              <Icon
                className={`h-[18px] w-[18px] shrink-0 ${
                  active ? 'text-primary' : 'text-gray-500 group-hover:text-gray-300'
                }`}
              />
            )}
            {!collapsed && <span className="truncate">{label}</span>}
          </>
        );
      }}
    </NavLink>
  );
}

function NavGroup({ group, collapsed }) {
  const location = useLocation();
  const isChildActive = group.children.some((c) => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(() => {
    if (isChildActive) return true;
    return localStorage.getItem(`sidebar-group-${group.label}`) !== '0';
  });

  const toggle = () => {
    setOpen((current) => {
      localStorage.setItem(`sidebar-group-${group.label}`, current ? '0' : '1');
      return !current;
    });
  };

  const GroupIcon = NAV_ICONS[`/${group.label.toLowerCase()}`] || NAV_ICONS.default;

  // No room to show nested items when the sidebar itself is icon-only — link straight to the
  // first visible child instead of trying to render a flyout.
  if (collapsed) {
    return <NavRow path={group.children[0].path} label={group.label} isActive={isChildActive} collapsed />;
  }

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        className={`group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          isChildActive ? 'text-white' : 'text-gray-400 hover:bg-white/[0.04] hover:text-gray-100'
        }`}
      >
        <GroupIcon className={`h-[18px] w-[18px] shrink-0 ${isChildActive ? 'text-primary' : 'text-gray-500 group-hover:text-gray-300'}`} />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <IconChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {group.children.map((child) => (
            <NavRow key={child.path} path={child.path} label={child.label} collapsed={false} nested />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { can } = usePermission();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === '1');

  const toggle = () => {
    setCollapsed((current) => {
      localStorage.setItem('sidebar-collapsed', current ? '0' : '1');
      return !current;
    });
  };

  const visibleItems = navItems
    .map((item) => {
      if (item.children) {
        const children = item.children.filter((c) => !c.perm || can(c.perm));
        return children.length > 0 ? { ...item, children } : null;
      }
      return !item.perm || can(item.perm) ? item : null;
    })
    .filter(Boolean);

  return (
    <aside
      className={`${
        collapsed ? 'w-[76px]' : 'w-64'
      } sticky top-0 h-screen shrink-0 flex flex-col bg-ink text-gray-300 shadow-panel transition-[width] duration-200 ease-out`}
    >
      <div className={`flex items-center gap-3 px-5 py-5 ${collapsed ? 'justify-center px-0' : ''}`}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-dark text-sm font-bold text-white shadow-[0_4px_14px_-2px_rgba(220,38,38,0.55)]">
          D
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">Driver CRM</p>
            <p className="truncate text-[11px] text-gray-500">Recruitment Admin</p>
          </div>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {visibleItems.map((item) =>
          item.children ? (
            <NavGroup key={item.label} group={item} collapsed={collapsed} />
          ) : (
            <NavRow key={item.path} path={item.path} label={item.label} collapsed={collapsed} />
          )
        )}
      </nav>

      <div className="border-t border-white/[0.06] p-3">
        <button
          type="button"
          onClick={toggle}
          title={collapsed ? 'Expand sidebar' : undefined}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium text-gray-500 transition-colors hover:bg-white/[0.04] hover:text-gray-300 ${
            collapsed ? 'justify-center px-0' : ''
          }`}
        >
          <IconPanelToggle className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
